<template>
  <v-alert
    v-if="cameraLoginError"
    type="warning"
    elevation="2"
    icon="warning"
    prominent
  >
    {{ cameraLoginError }}
  </v-alert>
  <div
    v-else-if="!isAuthenticated"
    :class="!iFrameLogin ? 'd-flex align-center justify-center' : ''"
  >
    <v-form
      ref="form"
      v-model="valid"
      lazy-validation
      style="width: 100%"
      :class="iFrameLogin ? '' : 'height-300 align-center justify-center'"
    >
      <v-card-text :class="smAndDown ? 'mx-16' :''">
        <template v-if="!iFrameLogin">
          <p class="text-center mb-4" >
            {{ t('login_to_your_account', { cameraProviderName }) }}
          </p>
        </template>
        <!-- Custom Login (DtIQ) -->
        <v-row v-if="isLoginThroughTheirCrappyPortal" class="pa-3">
          <v-btn color="primary" dark block tile @click="login">
            {{ t('login_portal') }}
          </v-btn>
          <p class="error" v-if="error">{{ error }}</p>
        </v-row>
        <div v-else-if="iFrameLogin" style="height: 600px">
          <iframe class="full" :src="iFrameLogin"></iframe>
        </div>
        <v-row v-else align="center" justify="center">
          <v-col>
            <v-text-field
              :label="t('username')"
              density="compact"
              color="primary"
              autocomplete="false"
              :rules="usernameRules"
              :error-messages="error"
              @focus="clearError"
              v-model="username"
              validate-on-blur
              variant="outlined"
              append-inner-icon="mdi-account-box-outline"
            />
            <v-text-field
              :label="t('password')"
              variant="outlined"
              density="compact"
              color="primary"
              autocomplete="false"
              :rules="passwordRules"
              v-model="password"
              validate-on-blur
              :append-inner-icon="
                showPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline'
              "
              @click:append-inner="togglePassword"
              :type="showPassword ? 'text' : 'password'"
            />
            <v-btn
              color="primary"
              dark
              block
              tile
              @click="login"
              :loading="loading"
            >
              {{ t('login') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-form>
  </div>
  <div v-else>
    <slot />
  </div>
</template>

<script setup>
import { computed, ref, toRefs, watch } from 'vue';
import { CameraIntegration } from '../helpers/camera/base';
import { ProviderMap } from '../helpers/camera-utils';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  camera: {
    type: CameraIntegration,
    required: true,
  },
});
const { camera } = toRefs(props);
const { smAndDown } = useDisplay();

const username = ref('');
const password = ref('');

const usernameRules = ref([
  (v) => !!v || t('username_required'),
  (v) => (v && v.length <= 255) || t('username_too_long'),
]);
const passwordRules = ref([(v) => !!v || t('password_required')]);

const valid = ref(true);
const loading = ref(false);
const showPassword = ref(false);
const error = ref('');
const cameraCredentialError = ref(null);

const isAuthenticated = computed(() => camera.value.isAuthenticated);
const iFrameLogin = computed(() => camera.value.iFrameLogin);
const cameraProvider = computed(() => camera.value?.camera?.camProvider);
const cameraProviderName = computed(() => {
  if (cameraProvider.value === 'itech' || cameraProvider.value === 'ITECH') {
    // itech/3xLogic can have many different providers so keep it generic
    return '3xLogic';
  } else {
    return ProviderMap[cameraProvider.value] || cameraProvider.value;
  }
});
const isLoginThroughTheirCrappyPortal = computed(() => camera.value?.isLoginThroughTheirCrappyPortal);
const cameraLoginError = computed(() => cameraCredentialError.value || camera.value?.authError);

const form = ref(null);
async function login() {
  if (!form.value.validate()) return;
  loading.value = true;

  // remove trailing whitespaces on username or password
  username.value = username.value.trim();
  password.value = password.value.trim();

  try {
    const response = await camera.value.submitCredentials(username.value, password.value);
  } catch (err) {
    error.value = err.message;
  } finally {
    loading.value = false;
  }
}

function clearError() {
  error.value = '';
}

function togglePassword() {
  showPassword.value = !showPassword.value;
}

watch(camera, () => {
  if (camera.value) {
    try {
      camera.value.checkForCredentials();
    } catch (err) {
      cameraCredentialError.value = err.message;
    }
  }
}, { immediate: true });
</script>

<style scoped lang="scss">
.full {
  width: 100%;
  height: 100%;
}
</style>
<i18n>
  {
    "en": {
      "login_to_your_account": "Login to your {cameraProviderName} account to view the security cameras",
      "login_portal": "Login Portal",
      "username": "Username",
      "password": "Password",
      "login": "Login",
      "username_required": "Username is required",
      "username_too_long": "Username must be less than 255 characters",
      "password_required": "Password is required"
    },
    "es": {
      "login_to_your_account": "Inicie sesión en su cuenta de {cameraProviderName} para ver las cámaras de seguridad",
      "login_portal": "Portal de Inicio de Sesión",
      "username": "Usuario",
      "password": "Contraseña",
      "login": "Iniciar Sesión",
      "username_required": "El usuario es obligatorio",
      "username_too_long": "El usuario debe tener menos de 255 caracteres",
      "password_required": "La contraseña es obligatoria"
    },
    "fr": {
      "login_to_your_account": "Connectez-vous à votre compte {cameraProviderName} pour voir les caméras de sécurité",
      "login_portal": "Portail de Connexion",
      "username": "Nom d'utilisateur",
      "password": "Mot de passe",
      "login": "Connexion",
      "username_required": "Le nom d'utilisateur est requis",
      "username_too_long": "Le nom d'utilisateur doit comporter moins de 255 caractères",
      "password_required": "Le mot de passe est requis"
    },
    "fr-ca": {
      "login_to_your_account": "Connectez-vous à votre compte {cameraProviderName} pour voir les caméras de sécurité",
      "login_portal": "Portail de Connexion",
      "username": "Nom d'utilisateur",
      "password": "Mot de passe",
      "login": "Connexion",
      "username_required": "Le nom d'utilisateur est requis",
      "username_too_long": "Le nom d'utilisateur doit comporter moins de 255 caractères",
      "password_required": "Le mot de passe est requis"
    }
  }
</i18n>
